import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';

/**
 * The walk-through of the team form. The whole page goes dark except the one
 * box the step is about; that box keeps its colour, gets a blue ring, and a
 * blue card beside it says what to do there and why.
 *
 * The dark is a huge box-shadow on an empty box sitting over the target, so
 * nothing covers the page: the field being pointed at stays usable, and
 * someone can fill it in while the card is open.
 *
 * Steps find their box by the data-tour attribute, set through the `tour` prop
 * on Section and Field in ui.jsx. A step whose box is not on the page (a
 * section that only exists while editing, say) still shows, centred.
 */

const SEEN_KEY = 'agentTeam.setupTour.seen';

// A private window, or site data turned off, throws on both of these.
export const tourSeen = () => {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true; // cannot remember it, so do not open it unasked
  }
};

const rememberSeen = () => {
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* nothing to do: it just opens again next time */
  }
};

// `note` is the live bit — what is actually missing on the team in front of you.
const STEPS = [
  {
    key: 'brand',
    title: 'Start with the brand',
    body: 'Any brand you already added in MARS, on the dashboard, in competitor spy or in affiliate gap. Its verticals and markets are read straight from there, so the Scout searches on real data rather than anything typed twice.',
    note: (s) => (s.brandChosen ? null : 'No brand chosen yet.')
  },
  {
    key: 'pitch',
    title: 'Say why an affiliate should list it',
    body: 'The welcome offer, the payment methods, how fast withdrawals are, how well it converts: whatever is true and specific. This text is the only thing the agents are allowed to claim about the brand, so anything not in it never reaches an affiliate.'
  },
  {
    key: 'deals',
    title: 'Give it a deal to offer',
    body: 'Describe the deal in plain words in the Deals tab. The deal desk asks for whatever is missing, saves it, and the Negotiator may then move only inside those limits. Anything beyond them stops and waits for you. One deal is enough to start.',
    note: (s) => (s.dealsChosen ? null : 'No deal chosen yet.')
  },
  {
    key: 'mailboxes',
    title: 'Pick the mailbox it writes from',
    body: 'Emails go out over your own SMTP, from your own address, and replies come back in over IMAP every few minutes. Without IMAP the team can send but will never see an answer, so the conversation stops dead after the first email.',
    note: (s) => {
      if (!(s.mailboxes || []).length) return 'No mailbox is connected. Add one in Email Settings, verify it, then come back here.';
      if (!s.readableMailbox) return 'A mailbox is connected, but none of them can read replies (no IMAP).';
      if (!s.sendersChosen) return 'No mailbox chosen for this team yet.';
      if (!s.repliesReadable) return 'The mailbox this team sends from cannot read replies. Add one with IMAP, or every thread stops after the first email.';
      return null;
    }
  },
  {
    key: 'pace',
    title: 'Set the pace',
    body: 'How long it waits before answering a reply, which hours of which days it may send in, and how far apart the follow-ups sit. A mailbox firing hundreds of cold emails a day is a mailbox on its way to the spam folder, so these caps are shared across every team using it.'
  },
  {
    key: 'approvals',
    title: 'Decide what needs your sign-off',
    body: 'On a first team, start with review deals: you see every draft that names terms, and the ordinary back-and-forth goes out on its own. Whatever you pick here, a draft that breaks a rule or passes a deal limit always waits for a person.'
  },
  {
    key: 'start',
    // The button says Create team on a new one and Save changes on an old one,
    // so the step has to as well.
    title: 'Save it, then press Start',
    body: (s) => (s.editing
      ? 'Changes are saved to the team but do not start it. Nothing is scouted, written or sent until someone presses Start in the team list, and a server restart pauses every running team again on purpose, so no agents keep working quietly after a deploy.'
      : 'A team is created paused. Nothing is scouted, written or sent until someone presses Start in the team list, and a server restart pauses every running team again on purpose, so no agents keep working quietly after a deploy.')
  }
];

export const TOUR_STEPS = STEPS.length;

const PAD = 8;    // how far the ring sits outside the box
const GAP = 12;   // the card's clearance from the box and from the screen edge
const CARD_W = 360;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const same = (a, b) => (!a && !b) || (a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

const SetupTour = ({ status = {}, onClose }) => {
  const [i, setI] = useState(0);
  const [box, setBox] = useState(null);
  const [cardH, setCardH] = useState(220);
  const [view, setView] = useState({ w: window.innerWidth, h: window.innerHeight });
  const cardRef = useRef(null);
  const step = STEPS[i];

  // Closed halfway counts as seen too: it opens by itself only once, and the
  // button on the guide brings it back whenever someone wants it.
  const finish = useCallback(() => {
    rememberSeen();
    onClose();
  }, [onClose]);

  // Bring the step's box into view. The measuring loop below picks the
  // movement up frame by frame, so the ring and the card ride the scroll.
  useEffect(() => {
    const el = document.querySelector(`[data-tour="${STEPS[i].key}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [i]);

  // The page keeps moving under us: smooth scrolling, a section opening, the
  // window resizing. Re-read the rectangle, the card's own height and the
  // viewport every frame, and re-render only when one of them has changed.
  useEffect(() => {
    let raf;
    const read = () => {
      const el = document.querySelector(`[data-tour="${STEPS[i].key}"]`);
      const r = el ? el.getBoundingClientRect() : null;
      const next = r ? { top: r.top, left: r.left, width: r.width, height: r.height } : null;
      setBox((prev) => (same(prev, next) ? prev : next));
      const h = cardRef.current ? cardRef.current.offsetHeight : 0;
      if (h) setCardH((prev) => (prev === h ? prev : h));
      setView((prev) => (prev.w === window.innerWidth && prev.h === window.innerHeight
        ? prev
        : { w: window.innerWidth, h: window.innerHeight }));
      raf = window.requestAnimationFrame(read);
    };
    raf = window.requestAnimationFrame(read);
    return () => window.cancelAnimationFrame(raf);
  }, [i]);

  // The arrow keys move the tour, but not while someone is typing in the very
  // field it is pointing at — there they move the cursor, as they should.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        finish();
        return;
      }
      const el = e.target;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, STEPS.length - 1));
      else if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  const { w: vw, h: vh } = view;
  const width = Math.min(CARD_W, vw - GAP * 2);

  // Under the box when the card fits there, over it when it does not, and
  // pinned to the bottom of the screen when neither fits — a section taller
  // than the window has no room either side of it.
  const place = () => {
    if (!box) return { left: Math.max(GAP, (vw - width) / 2), top: Math.max(GAP, (vh - cardH) / 2), arrow: null };
    const left = clamp(box.left - PAD, GAP, Math.max(GAP, vw - width - GAP));
    const under = box.top + box.height + PAD + GAP;
    const over = box.top - PAD - GAP - cardH;
    if (under + cardH + GAP <= vh) return { left, top: under, arrow: 'up' };
    if (over >= GAP) return { left, top: over, arrow: 'down' };
    return { left, top: Math.max(GAP, vh - cardH - GAP), arrow: null };
  };
  const card = place();
  const arrowLeft = box ? clamp(box.left + box.width / 2 - card.left, 18, width - 18) : 0;
  const body = typeof step.body === 'function' ? step.body(status) : step.body;
  const note = step.note ? step.note(status) : null;

  return createPortal(
    <>
      {box && (
        <div
          style={{
            position: 'fixed',
            top: box.top - PAD,
            left: box.left - PAD,
            width: box.width + PAD * 2,
            height: box.height + PAD * 2,
            borderRadius: 14,
            boxShadow: '0 0 0 2px rgb(59 130 246), 0 0 0 9999px rgba(2, 6, 23, 0.72)',
            pointerEvents: 'none',
            transition: 'top 150ms ease-out, left 150ms ease-out, width 150ms ease-out, height 150ms ease-out',
            zIndex: 9998
          }}
        />
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-label="Setting up a team"
        aria-live="polite"
        style={{ position: 'fixed', left: card.left, top: card.top, width, zIndex: 9999 }}
        className="rounded-xl bg-blue-600 p-4 text-white shadow-2xl"
      >
        {card.arrow && (
          <span
            style={{ position: 'absolute', left: arrowLeft - 6, [card.arrow === 'up' ? 'top' : 'bottom']: -6, width: 12, height: 12, background: 'rgb(37 99 235)', transform: 'rotate(45deg)' }}
          />
        )}

        <div className="mb-1 flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-200">Step {i + 1} of {STEPS.length}</span>
          <button type="button" onClick={finish} title="Close the walk-through" className="ml-auto -mr-1 -mt-1 rounded p-1 text-blue-200 hover:bg-blue-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <h4 className="text-sm font-semibold">{step.title}</h4>
        <p className="mt-1 text-xs leading-relaxed text-blue-50">{body}</p>
        {note && (
          <p className="mt-2 rounded-lg bg-blue-700/60 px-2 py-1.5 text-xs font-medium text-amber-100">{note}</p>
        )}

        <div className="mt-3 flex items-center gap-2">
          <div className="flex gap-1">
            {STEPS.map((s, n) => (
              <button key={s.key} type="button" onClick={() => setI(n)} title={s.title} aria-label={s.title} className="p-1">
                <span className={`block h-1.5 w-1.5 rounded-full ${n === i ? 'bg-white' : 'bg-blue-400 hover:bg-blue-200'}`} />
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {i > 0 && (
              <button type="button" onClick={() => setI(i - 1)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-blue-100 hover:bg-blue-500">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            {i < STEPS.length - 1 ? (
              <button type="button" onClick={() => setI(i + 1)}
                className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                Next <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button type="button" onClick={finish}
                className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                <Check className="h-3.5 w-3.5" /> Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SetupTour;
